import { useState, useEffect } from 'react';
import { Musikk } from '@/services/api';

interface UseEditSongFormProps {
  musikk: Musikk | null;
}

interface EditSongFormData {
  tittel: string;
  artist: string;
  album: string;
  utgivelsesaar: number | '';
  bpm: number | '';
  takt: string;
  lengdeMinutter: number | '';
  lengdeSekunder: number | '';
  grunntone: string;
  toneartType: string;
  categories: string[];
  tekstOgAkkorder: string;
  notater: string;
  publisert: boolean;
}

export const useEditSongForm = ({ musikk }: UseEditSongFormProps) => {
  const [formData, setFormData] = useState<EditSongFormData>({
    tittel: '',
    artist: '',
    album: '',
    utgivelsesaar: '',
    bpm: '',
    takt: '',
    lengdeMinutter: '',
    lengdeSekunder: '',
    grunntone: '',
    toneartType: 'dur',
    categories: [],
    tekstOgAkkorder: '',
    notater: '',
    publisert: false,
  });

  useEffect(() => {
    if (musikk) {
      setFormData({
        tittel: musikk.tittel,
        artist: musikk.artist || '',
        album: musikk.album || '',
        utgivelsesaar: musikk.utgivelsesaar || '',
        bpm: musikk.bpm || '',
        takt: musikk.takt || '',
        lengdeMinutter: musikk.lengde_minutter || '',
        lengdeSekunder: musikk.lengde_sekunder || '',
        grunntone: musikk.grunntone || '',
        toneartType: musikk.toneart_type || 'dur',
        categories: musikk.kategorier.map(k => k.navn),
        tekstOgAkkorder: musikk.tekst_og_akkorder || '',
        notater: musikk.notater || '',
        publisert: musikk.publisert || false,
      });
    } else {
      // La feltene være tomme for ny sang
      setFormData({
        tittel: '',
        artist: '',
        album: '',
        utgivelsesaar: '',
        bpm: '',
        takt: '',
        lengdeMinutter: '',
        lengdeSekunder: '',
        grunntone: '',
        toneartType: 'dur',
        categories: [],
        tekstOgAkkorder: '',
        notater: '',
        publisert: false,
      });
    }
  }, [musikk]);

  const updateField = <K extends keyof EditSongFormData>(
    field: K,
    value: EditSongFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!formData.tittel || formData.tittel.trim().length === 0) {
      errors.push('Tittel er påkrevd');
    }
    
    if (!formData.artist || formData.artist.trim().length === 0) {
      errors.push('Artist er påkrevd');
    }
    
    if (formData.lengdeSekunder !== '' && (formData.lengdeSekunder < 0 || formData.lengdeSekunder >= 60)) {
      errors.push('Lengde sekunder må være mellom 0 og 59');
    }
    
    if (formData.bpm !== '' && (formData.bpm <= 0 || formData.bpm >= 400)) {
      errors.push('BPM må være mellom 1 og 399');
    }
    
    return { isValid: errors.length === 0, errors };
  };

  return {
    formData,
    updateField,
    validate,
  };
};

